import type { IPlan, IPlanKey } from '../types';

import { usePricingStore } from '../stores';
import { useCallback } from 'react';

interface IPricingPlanData {
  getPlanKeyFromId: (planId: IPlan['id']) => IPlanKey | undefined;
  getPlanNameByPlanKey: (planKey?: IPlanKey) => IPlan['displayName'] | undefined;
  getPlanIdByPlanKey: (planKey?: IPlanKey) => IPlan['id'] | undefined;
  getPlanDataByPlanKey: (planKey?: IPlanKey) => IPlan | undefined;
  getPlanDataByPlanId: (planId: IPlan['id']) => IPlan | undefined;
}

export const usePricingPlanData = (): IPricingPlanData => {
  const listPlans = usePricingStore((state) => state.listPlans);

  const getPlanKeyFromId = useCallback(
    (planId: string | number): IPlanKey | undefined => {
      if (!listPlans) return;

      const plan = listPlans.find((p) => p.id === planId || p.planKey === planId);
      return plan?.planKey;
    },
    [listPlans],
  );

  const getPlanDataByPlanKey = useCallback(
    (planKey?: IPlanKey) => {
      return listPlans?.find((p) => p.planKey === planKey);
    },
    [listPlans],
  );

  const getPlanDataByPlanId = useCallback(
    (planId: IPlan['id']) => {
      return listPlans?.find((p) => p.id === planId);
    },
    [listPlans],
  );

  const getPlanNameByPlanKey = useCallback(
    (planKey?: IPlanKey) => {
      const planData = getPlanDataByPlanKey(planKey);
      if (!planData) return;

      const displayName = planData.displayName;
      const isTrial = planData.isTrial;
      if (isTrial) return displayName?.replace('Trial', '').trim();

      return displayName;
    },
    [getPlanDataByPlanKey],
  );

  const getPlanIdByPlanKey = useCallback(
    (planKey?: IPlanKey) => {
      return getPlanDataByPlanKey(planKey)?.id;
    },
    [getPlanDataByPlanKey],
  );

  return {
    getPlanKeyFromId,
    getPlanDataByPlanKey,
    getPlanNameByPlanKey,
    getPlanIdByPlanKey,
    getPlanDataByPlanId,
  };
};
